import React, { useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { 
  Clock, MapPin, Check, X, ArrowRight,
  Users, CreditCard, ChevronRight, Package, HelpCircle, Calendar, Timer, Lock, Shield
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { activitiesData } from '../../data/activities';
import { activitiesDataEN } from '../../data/activitiesEN';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import StickyWhatsAppMobile from '../landing/StickyWhatsAppMobile';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getWhatsAppLink, WHATSAPP_BASE_URL } from '../../utils/whatsapp';

// WhatsApp SVG Icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ActivityPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const { language, isEnglish } = useLanguage();
  const { t } = useTranslation();
  
  // Use English or French data based on language
  const activityDataSource = isEnglish ? activitiesDataEN : activitiesData;
  const activity = activityDataSource[slug];
  const langPrefix = isEnglish ? '/en' : '';

  // Scroll to top on page load / route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (!activity) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 mb-4">
            {isEnglish ? 'Activity not found' : 'Activité non trouvée'}
          </h1>
          <Link to={langPrefix || '/'} className="text-teal-600 hover:underline">
            {t('activity.home')}
          </Link>
        </div>
      </div>
    );
  }

  // WhatsApp link for booking this specific activity
  const whatsappBookingLink = getWhatsAppLink(activity.title, language);

  // WhatsApp link for support/help
  const whatsappHelpLink = `${WHATSAPP_BASE_URL}?text=${encodeURIComponent(
    isEnglish 
      ? `Hello 👋\nI have a question about ${activity.title}.`
      : `Bonjour 👋\nJ'ai une question concernant ${activity.title}.`
  )}`;

  // Check if this is Orange Bay (new template) or other activities (legacy)
  const isNewTemplate = activity.experience !== undefined;

  // Get Stripe link for this activity
  const stripeLink = activity.stripeLink;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-10 md:py-16 bg-gradient-to-b from-teal-50 to-white overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-teal-100/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-cyan-100/30 rounded-full blur-3xl" />
          
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Back to menu link */}
            <Link 
              to={langPrefix || '/'} 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-teal-600 transition-colors mb-4 group"
            >
              <ArrowRight className="w-4 h-4 rotate-180 group-hover:-translate-x-1 transition-transform" />
              <span className="font-medium">{t('activity.backToMenu')}</span>
            </Link>

            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-slate-500 mb-6">
              <Link to={langPrefix || '/'} className="hover:text-teal-600 transition-colors">{t('activity.home')}</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-teal-600">{activity.title}</span>
            </nav>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Left: Content */}
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
                  {activity.h1}
                </h1>

                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  {activity.shortDescription}
                </p>

                {/* Important Note (for dolphins) */}
                {activity.importantNote && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                    <p className="text-amber-800 text-sm font-medium">
                      ⚠️ {activity.importantNote}
                    </p>
                  </div>
                )}

                {/* Available Days (for Luxor) */}
                {activity.availableDays && (
                  <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-6">
                    <p className="text-sky-800 text-sm font-medium">
                      📅 {t('activity.availableDays')} : {activity.availableDays}
                    </p>
                  </div>
                )}

                {/* Price Block */}
                <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mb-6">
                  {activity.priceLabel ? (
                    <div className="mb-2">
                      <span className="text-2xl font-bold text-teal-600">{activity.priceLabel}</span>
                    </div>
                  ) : (
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-4xl font-bold text-teal-600">{activity.price}€</span>
                      <span className="text-slate-500">{t('activity.perPerson')}</span>
                    </div>
                  )}
                  {activity.priceNote && (
                    <div className="space-y-1">
                      {activity.priceNote.split('•').map((note, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-emerald-600">
                          <Check className="w-4 h-4 flex-shrink-0" />
                          <span className="font-medium text-sm">{note.trim()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* SECONDARY CTA - Stripe Deposit (ABOVE WhatsApp, darker bubble) */}
                <div className="bg-slate-700 rounded-xl p-4 mb-5 border border-slate-600 relative z-10">
                  <div className="flex flex-col items-center text-center">
                    <a
                      href={stripeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-transparent border border-slate-400 text-slate-200 hover:bg-slate-600 hover:text-white active:bg-slate-500 text-sm px-5 py-2.5 rounded-lg transition-all duration-300 touch-manipulation inline-block text-center"
                      style={{ WebkitTapHighlightColor: 'rgba(100, 116, 139, 0.3)' }}
                    >
                      {isEnglish 
                        ? `Book – deposit ${activity.depositAmount} €`
                        : `Réserver – acompte ${activity.depositAmount} €`
                      }
                    </a>
                    <p className="text-xs text-slate-400 mt-2">
                      🔒 {isEnglish ? 'Secure payment · Deposit only' : 'Paiement sécurisé · Acompte uniquement'}
                    </p>
                  </div>
                </div>

                {/* Optional (for Cairo/Luxor) */}
                {activity.optional && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                    <p className="text-purple-800 text-sm font-medium">
                      ✨ {t('activity.option')} : {activity.optional}
                    </p>
                  </div>
                )}

                {/* PRIMARY CTA - WhatsApp Booking (GREEN) with founder photo */}
                <div className="space-y-4 mb-4">
                  <div className="flex items-center gap-3">
                    <img
                      src="https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,c_fill,w_120,h_120/Maximehurghadaexcursionfounder_rohsvv.jpg"
                      alt="Maxime"
                      className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md flex-shrink-0"
                    />
                    <Button
                      asChild
                      size="lg"
                      className="w-full sm:w-auto bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6 rounded-full shadow-lg shadow-emerald-200 transition-all duration-300 hover:shadow-emerald-300 hover:scale-[1.02]"
                    >
                      <a href={whatsappBookingLink} target="_blank" rel="noopener noreferrer">
                        <WhatsAppIcon className="w-5 h-5 mr-2" />
                        <span className="hidden sm:inline">WhatsApp</span>
                        <span className="sm:hidden">WhatsApp</span>
                      </a>
                    </Button>
                  </div>
                  
                  {/* Explanatory text */}
                  <p className="text-xs text-slate-400 max-w-md">
                    {isEnglish 
                      ? 'Deposit amount may vary depending on activity and date. Balance to be paid on site on the day of the excursion.'
                      : 'Le montant de l\'acompte peut varier selon l\'activité et la date. Le solde est à régler sur place le jour de l\'excursion.'}
                  </p>
                </div>

                {/* SECONDARY CTA - WhatsApp Support */}
                <div className="mt-4">
                  <a 
                    href={whatsappHelpLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-slate-600 hover:text-emerald-600 transition-colors text-sm font-medium"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    {t('activity.needHelp')}
                  </a>
                </div>
              </div>

              {/* Right: Images Stack (3 images vertically) */}
              <div className="space-y-4">
                {activity.images && activity.images.length > 0 ? (
                  activity.images.map((imageUrl, index) => (
                    <div 
                      key={index} 
                      className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg"
                    >
                      <img
                        src={imageUrl}
                        alt={`${activity.title} - Image ${index + 1}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                    </div>
                  ))
                ) : (
                  /* Fallback placeholders if no images */
                  <>
                    <div className="aspect-[4/3] bg-slate-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-200">
                      <Camera className="w-12 h-12 text-slate-300" />
                    </div>
                    <div className="aspect-[4/3] bg-slate-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-200">
                      <Camera className="w-10 h-10 text-slate-300" />
                    </div>
                    <div className="aspect-[4/3] bg-slate-100 rounded-2xl flex items-center justify-center border-2 border-dashed border-slate-200">
                      <Camera className="w-10 h-10 text-slate-300" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Practical Info Cards */}
        <section className="py-10 md:py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { 
                  icon: Clock, 
                  label: t('activity.duration'), 
                  value: isNewTemplate ? activity.practicalInfo.duration : activity.duration,
                  bg: 'bg-teal-50',
                  iconColor: 'text-teal-500'
                },
                { 
                  icon: MapPin, 
                  label: t('activity.departure'), 
                  value: isNewTemplate ? activity.practicalInfo.departurePoint : 'Hotel',
                  bg: 'bg-cyan-50',
                  iconColor: 'text-cyan-500'
                },
                { 
                  icon: Calendar, 
                  label: t('activity.departureTime'), 
                  value: isNewTemplate ? activity.practicalInfo.departureTime : activity.departure,
                  bg: 'bg-emerald-50',
                  iconColor: 'text-emerald-500'
                },
                { 
                  icon: Timer, 
                  label: t('activity.return'), 
                  value: isNewTemplate ? activity.practicalInfo.returnTime : activity.return,
                  bg: 'bg-amber-50',
                  iconColor: 'text-amber-500'
                }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`${item.bg} rounded-xl p-4 text-center transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 cursor-default`}
                >
                  <item.icon className={`w-6 h-6 ${item.iconColor} mx-auto mb-2`} />
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">{item.label}</p>
                  <p className="font-semibold text-slate-800 text-sm">{item.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Experience / Déroulement */}
        <section className="py-10 md:py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-6">{t('activity.itinerary')}</h2>
            
            {isNewTemplate ? (
              <div className="bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-sm">
                <div className="space-y-4">
                  {activity.experience.map((step, index) => (
                    <div 
                      key={index}
                      className="flex gap-4 p-3 rounded-xl hover:bg-slate-50 transition-colors cursor-default group"
                    >
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-200 transition-colors">
                        <span className="text-sm font-bold text-teal-600">{index + 1}</span>
                      </div>
                      <p className="text-slate-700 leading-relaxed pt-1">{step}</p>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {activity.timeline?.map((item, index) => (
                  <div 
                    key={index}
                    className="flex gap-4 p-4 bg-white rounded-xl border border-slate-100 hover:border-teal-200 hover:shadow-sm transition-all duration-300"
                  >
                    <div className="w-16 h-10 rounded-lg bg-teal-100 flex items-center justify-center text-teal-700 font-bold text-sm flex-shrink-0">
                      {item.time}
                    </div>
                    <p className="text-slate-700 pt-2">{item.step}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Included / Not Included */}
        <section className="py-10 md:py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Included */}
              <div className="bg-emerald-50 rounded-2xl p-6 md:p-8 border border-emerald-100 transition-all duration-300 hover:shadow-md">
                <h3 className="text-xl font-bold text-slate-800 mb-5 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-700" />
                  </div>
                  {t('activity.included')}
                </h3>
                <ul className="space-y-3">
                  {activity.included.map((item, index) => (
                    <li 
                      key={index} 
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-emerald-100/50 transition-colors cursor-default"
                    >
                      <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Not Included */}
              <div className="bg-slate-50 rounded-2xl p-6 md:p-8 border border-slate-100 transition-all duration-300 hover:shadow-md">
                <h3 className="text-xl font-bold text-slate-800 mb-5 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                    <X className="w-5 h-5 text-slate-600" />
                  </div>
                  {t('activity.notIncluded')}
                </h3>
                <ul className="space-y-3">
                  {activity.notIncluded.map((item, index) => (
                    <li 
                      key={index} 
                      className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-100 transition-colors cursor-default"
                    >
                      <X className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Mid-page - Support WhatsApp */}
        <section className="py-10 md:py-12 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-4">{t('activity.question')}</h2>
            <p className="text-slate-600 mb-6">{t('activity.questionSubtext')}</p>
            <a 
              href={whatsappHelpLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-all duration-300 font-medium"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {t('activity.needHelp')}
            </a>
          </div>
        </section>

        {/* Trust Block */}
        <section className="py-10 md:py-12 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: WhatsAppIcon, title: t('activity.trustFrench'), desc: t('activity.trustFrenchDesc'), bg: 'bg-sky-50', iconBg: 'bg-sky-100', iconColor: 'text-sky-600' },
                { icon: Users, title: t('activity.trustPartners'), desc: t('activity.trustPartnersDesc'), bg: 'bg-amber-50', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
                { icon: CreditCard, title: t('activity.trustPayment'), desc: t('activity.trustPaymentDesc'), bg: 'bg-emerald-50', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' }
              ].map((item, index) => (
                <div 
                  key={index}
                  className={`${item.bg} rounded-xl p-5 transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 cursor-default`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center flex-shrink-0`}>
                      <item.icon className={`w-6 h-6 ${item.iconColor}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-800">{item.title}</h3>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Upsell Packs */}
        <section className="py-10 md:py-12 bg-gradient-to-b from-slate-50 to-cyan-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 rounded-full text-cyan-700 font-medium mb-4">
              <Package className="w-4 h-4" />
              {t('activity.packsBadge')}
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-3">
              {t('activity.packsTitle')}
            </h2>
            <p className="text-slate-600 mb-6 max-w-xl mx-auto">
              {t('activity.packsSubtitle')}
            </p>
            <Button
              asChild
              className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-6 py-5 shadow-lg transition-all duration-300 hover:scale-[1.02]"
            >
              <Link to={`${langPrefix}/packs`}>
                {t('activity.viewPacks')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-10 md:py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
              <HelpCircle className="w-6 h-6 text-teal-500" />
              {t('activity.faqTitle')}
            </h2>
            
            <Accordion type="single" collapsible className="space-y-3">
              {activity.faq.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`item-${index}`}
                  className="bg-slate-50 rounded-xl border border-slate-100 px-5 hover:border-teal-200 transition-colors data-[state=open]:border-teal-300 data-[state=open]:bg-teal-50/50"
                >
                  <AccordionTrigger className="text-left text-slate-800 font-semibold py-4 hover:no-underline hover:text-teal-600 transition-colors [&[data-state=open]]:text-teal-600">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-4 leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Final CTA - WhatsApp Booking (GREEN) */}
        <section className="py-10 md:py-12 bg-gradient-to-b from-white to-teal-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-slate-800 mb-3">{t('activity.readyToBook')}</h2>
            <p className="text-slate-600 mb-6">
              {t('activity.readyToBookSubtitle')}
            </p>
            
            {/* Secondary CTA - Stripe Deposit (ABOVE WhatsApp, darker bubble) */}
            <div className="bg-slate-700 rounded-xl p-4 mb-5 border border-slate-600 max-w-xs mx-auto relative z-10">
              <div className="flex flex-col items-center text-center">
                <a
                  href={stripeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-transparent border border-slate-400 text-slate-200 hover:bg-slate-600 hover:text-white active:bg-slate-500 text-sm px-5 py-2.5 rounded-lg transition-all duration-300 touch-manipulation inline-block text-center"
                  style={{ WebkitTapHighlightColor: 'rgba(100, 116, 139, 0.3)' }}
                >
                  {isEnglish 
                    ? `Book – deposit ${activity.depositAmount} €`
                    : `Réserver – acompte ${activity.depositAmount} €`
                  }
                </a>
                <p className="text-xs text-slate-400 mt-2 pointer-events-none">
                  🔒 {isEnglish ? 'Secure payment · Deposit only' : 'Paiement sécurisé · Acompte uniquement'}
                </p>
              </div>
            </div>
            
            {/* Primary CTA - WhatsApp (GREEN) with founder photo */}
            <div className="space-y-4 mb-6">
              <div className="flex items-center justify-center gap-3">
                <img
                  src="https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,c_fill,w_120,h_120/Maximehurghadaexcursionfounder_rohsvv.jpg"
                  alt="Maxime"
                  className="w-10 h-10 rounded-full object-cover border-2 border-white shadow-md"
                />
                <Button
                  asChild
                  size="lg"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white text-lg px-8 py-6 rounded-full shadow-lg shadow-emerald-200 transition-all duration-300 hover:shadow-emerald-300 hover:scale-[1.02]"
                >
                  <a href={whatsappBookingLink} target="_blank" rel="noopener noreferrer">
                    <WhatsAppIcon className="w-5 h-5 mr-2" />
                    <span className="hidden sm:inline">WhatsApp</span>
                    <span className="sm:hidden">WhatsApp</span>
                  </a>
                </Button>
              </div>
            </div>
            
            {/* Secondary - WhatsApp Support */}
            <a 
              href={whatsappHelpLink} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-slate-500 hover:text-emerald-600 transition-colors text-sm"
            >
              <WhatsAppIcon className="w-4 h-4" />
              {t('activity.needHelp')}
            </a>
          </div>
        </section>
      </main>

      <StickyWhatsAppMobile />
      <Footer />
    </div>
  );
};

export default ActivityPage;
